import { api, API_URL, isApiError, problemKey } from '@/shared/api';
import { unwrap } from '@/shared/api/envelope';

import {
    toPublicReport,
    toPublicTenant,
    toTranslation,
    type PublicLanguage,
    type PublicReport,
    type PublicTenant,
    type PublicTranslation,
} from './model';
import {
    publicReportProblemExtensionsSchema,
    publicReportResourceSchema,
    publicTranslationResourceSchema,
    type PublicLetterheadResource,
} from './types';

/**
 * De openbare rapportlink (`02-api-contract.md` §Rapportage, "Publiek").
 *
 * Geen sessie, geen account: het token uit de link is de sleutel. De client
 * stuurt daarom geen token en geen omgeving mee (`skipAuth`), ook niet als er
 * in hetzelfde tabblad iemand van kantoor is ingelogd.
 *
 * - Antwoorden gaan **niet** in de offline cache (`cacheKey: false`): een
 *   rapport van een koper hoort niet op een gedeeld apparaat achter te blijven.
 * - De http-client herhaalt niets (`retryAttempts: 1`). Na te veel foute codes
 *   antwoordt de API 429 met een wachttijd van een kwartier; die wachten we niet
 *   stil af, de pagina zegt het.
 */

const ACCESS_CODE_HEADER = 'X-Access-Code';

/** Zo lang mag een PDF duren: de eerste keer maakt de server hem nog. */
const PDF_TIMEOUT_MS = 180_000;

/**
 * Het pad onder de API van een downloadlink van de pagina (`documents[].url`),
 * met de ondertekening erbij; `null` voor een link die ergens anders heen wijst -
 * die volgt de browser gewoon.
 */
export function publicDocumentPath(url: string): string | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const base = new URL(`${API_URL}/`, window.location.href);
        const target = new URL(url, window.location.href);

        if (target.origin !== base.origin || !target.pathname.startsWith(base.pathname)) {
            return null;
        }

        return `/${target.pathname.slice(base.pathname.length)}${target.search}`;
    } catch {
        return null;
    }
}

export interface PublicReportQuery {
    token: string;
    language?: PublicLanguage;
    accessCode?: string | null;
}

function requestOptions(query: PublicReportQuery) {
    return {
        skipAuth: true,
        cacheKey: false as const,
        retryAttempts: 1,
        query: query.language ? { lang: query.language } : undefined,
        headers: query.accessCode ? { [ACCESS_CODE_HEADER]: query.accessCode } : undefined,
    };
}

function path(token: string, action = ''): string {
    return `/public/reports/${encodeURIComponent(token)}${action}`;
}

export const publicReportApi = {
    /** `GET /public/reports/{token}` - het rapport zoals de koper het ziet. */
    async load(query: PublicReportQuery): Promise<PublicReport> {
        return toPublicReport(
            unwrap(
                publicReportResourceSchema,
                await api.get(path(query.token), requestOptions(query)),
            ),
        );
    },

    /**
     * `GET /public/reports/{token}/translation?lang=` - hoe ver de vertaling in een
     * taal is. Telt niet als opening van de link: de pagina vraagt dit terwijl ze
     * wacht. `null` als de server de taal niet (meer) aanbiedt.
     */
    async translation(
        query: PublicReportQuery & { language: PublicLanguage },
    ): Promise<PublicTranslation | null> {
        return toTranslation(
            unwrap(
                publicTranslationResourceSchema,
                await api.get(path(query.token, '/translation'), requestOptions(query)),
            ),
        );
    },

    /**
     * `GET /reports/{report}/preview` - dezelfde pagina, bekeken vanaf kantoor
     * mét de sessie. Telt geen bezoek en vraagt geen toegangscode;
     * de downloadknop wijst naar de PDF die dezelfde sessie vraagt. Een
     * verlopen of ingetrokken link geeft dezelfde fout als bij de koper.
     * Net als de pagina zelf niet in de offline cache.
     */
    async preview(reportUuid: string): Promise<PublicReport> {
        return toPublicReport(
            unwrap(
                publicReportResourceSchema,
                await api.get(`/reports/${reportUuid}/preview`, {
                    cacheKey: false,
                    retryAttempts: 1,
                }),
            ),
        );
    },

    /**
     * De PDF achter een downloadknop, als bestand (`path` uit {@link publicDocumentPath}).
     * Via de client in plaats van de link te volgen: kan de server hem niet maken
     * (`503 pdf-unavailable`), dan zegt de pagina dat rustig in plaats van de
     * koper een scherm vol JSON te tonen.
     */
    async document(path: string, accessCode: string | null = null): Promise<Blob> {
        return api.get<Blob>(path, {
            ...requestOptions({ token: '', accessCode }),
            expect: 'blob',
            timeoutMs: PDF_TIMEOUT_MS,
        });
    },

    /** `POST …/share` - doorsturen naar makelaar of adviseur; zij hebben geen account nodig. */
    async share(query: PublicReportQuery & { email: string }): Promise<void> {
        await api.post(
            path(query.token, '/share'),
            { email: query.email },
            { ...requestOptions(query), expect: 'none', idempotencyKey: crypto.randomUUID() },
        );
    },

    /** `POST …/question` - een vraag aan de eigen inspecteur, niet aan een helpdesk. */
    async ask(
        query: PublicReportQuery & { message: string; email?: string | null },
    ): Promise<void> {
        await api.post(
            path(query.token, '/question'),
            { message: query.message, email: query.email ?? null },
            { ...requestOptions(query), expect: 'none', idempotencyKey: crypto.randomUUID() },
        );
    },
};

/**
 * Waarom een link niet werkt. Elke reden krijgt een eigen, rustige pagina in
 * de huisstijl - nooit een stacktrace en nooit een kale 404.
 */
export type PublicReportProblemKind =
    | 'notFound'
    | 'expired'
    | 'revoked'
    | 'codeRequired'
    | 'codeInvalid'
    | 'tooManyAttempts'
    | 'forwardingDisabled'
    | 'invalidInput'
    | 'offline'
    | 'unknown';

export interface PublicReportProblem {
    kind: PublicReportProblemKind;
    /** Bij een verlopen link: tot wanneer hij geldig was. */
    expiredAt: string | null;
    /** Bij een foute code: hoeveel pogingen er nog over zijn. */
    attemptsLeft: number | null;
    /** Bij te veel pogingen: na hoeveel minuten het weer kan. */
    retryAfterMinutes: number | null;
    /**
     * De huisstijl van het bedrijf achter de link, als het token bij een echte
     * link hoort: ook een verlopen link of het codeveld staat dan in zijn stijl.
     */
    tenant: PublicTenant | null;
}

/**
 * De API gebruikt op deze endpoints bewust **nooit** 401: een 401 zou de sessie
 * van een meekijkende collega uitloggen. Toegangscode en ingetrokken link zijn
 * allebei 403 en verschillen in `type`.
 */
export function publicReportProblemOf(error: unknown): PublicReportProblem {
    const empty: PublicReportProblem = {
        kind: 'unknown',
        expiredAt: null,
        attemptsLeft: null,
        retryAfterMinutes: null,
        tenant: null,
    };

    if (!isApiError(error)) {
        return empty;
    }

    if (error.kind === 'offline' || error.kind === 'network' || error.kind === 'timeout') {
        return { ...empty, kind: 'offline' };
    }

    const extensions = publicReportProblemExtensionsSchema.parse(error.problem);
    const problem: PublicReportProblem = {
        ...empty,
        tenant: extensions.tenant === null ? null : letterheadTenant(extensions.tenant),
    };

    switch (problemKey(error)) {
        case 'not-found':
            return { ...problem, kind: 'notFound' };
        case 'link-expired':
            return { ...problem, kind: 'expired', expiredAt: extensions.expired_at };
        case 'link-revoked':
            return { ...problem, kind: 'revoked' };
        case 'access-code-required':
            return { ...problem, kind: 'codeRequired' };
        case 'invalid-access-code':
            return { ...problem, kind: 'codeInvalid', attemptsLeft: extensions.attempts_left };
        case 'forwarding-disabled':
            return { ...problem, kind: 'forwardingDisabled' };
        default:
            break;
    }

    switch (error.status) {
        case 404:
            return { ...problem, kind: 'notFound' };
        case 410:
            return { ...problem, kind: 'expired', expiredAt: extensions.expired_at };
        case 422:
            return { ...problem, kind: 'invalidInput' };
        case 429:
            return {
                ...problem,
                kind: 'tooManyAttempts',
                retryAfterMinutes:
                    error.retryAfterMs === undefined
                        ? null
                        : Math.max(1, Math.ceil(error.retryAfterMs / 60_000)),
            };
        default:
            return problem;
    }
}

/** Een briefkop uit een foutantwoord als huisstijl voor de pagina. */
function letterheadTenant(letterhead: PublicLetterheadResource): PublicTenant {
    return toPublicTenant({
        ...letterhead,
        opening_hours: null,
        kvk: null,
        memberships: [],
        languages: ['nl'],
    });
}
